import React, { useEffect, useState } from "react"
import { Module, Permission, Role } from "../../types/dataModel"
import useFetch from "../../hooks/useFetch"
import { getAPIHeaders } from "../../common/Utils"
import { API } from "../../common/Constants"
import PreLoader from "../../core/common/preLoader"
import FormFooter from "../../core/common/formFooter"
import "../../style/css/modifyPermissions.css"

interface Props {
    roleID?: number
    onSuccess?: ()=>void
    readOnly?: boolean
}

const ModifyPermission = ({roleID, readOnly, onSuccess}:Props) => {

    const [role, setRole] = useState<Role>()
    const { isLoading, status, error, callFetch } = useFetch();
    const [msgBoxSett, mesgBoxSett] = useState<any>({
        message: "",
        type: "" //ERROR, INFO, SUCCESS
    })

    useEffect(() => {
        if(error){
          if(error.response?.data){
            mesgBoxSett( {message: error.response?.data, type:'ERROR'})
          } else{
            mesgBoxSett({message: 'Something went wrong please try again later.', type:'ERROR'})
          }
        }
    }, [error]);

    useEffect(()=>{
        if(roleID){
            mesgBoxSett( {message: "", type:'INFO'})
            fetch(roleID)
        }
    },[roleID])

    const fetch = (id:number) => {
        callFetch(`${API.ROLE}/${id}/permissions/tree`,
            'GET',
            getAPIHeaders(),
            null,
            null
        ).then((res)=>{
            setRole(res)
        }).catch((err) => {});
    }

    const update = (e:any) => {
        e.preventDefault()
        let permissions:number[] = []

        role?.module?.forEach((module:Module)=>{

            module?.permissions?.forEach((perm:Permission)=>{
                if(perm.id && perm.selected){
                    permissions.push(perm.id)
                }
            })

        })
        mesgBoxSett( {message: "", type:'INFO'} )

        if(!permissions?.length){
            mesgBoxSett( {message: "You have not selected any permissions.", type:'ERROR'} )
        } else if(role?.id){
            callFetch(`${API.ROLE}/${role?.id}/permissions`,
                'PUT',
                getAPIHeaders(),
                null,
                permissions
            ).then(()=>{
                if(status && status < 299){
                    mesgBoxSett( {message: "Permissions saved successfully", type:'SUCCESS'} )
                }
                if(role?.id){
                    fetch(role?.id)
                }
            }).catch((err) => {});
        }


    }

    const onCancel = () =>{
        if(role?.id){
            fetch(role?.id)
        }
    }

    const allSelected = (module: Module):boolean => {

        let isEmpty = module?.permissions?.filter((p:Permission)=>{
            return p?.type !== 'ALL'
        })?.length ? false : true

        if(isEmpty){
            return false
        }

        return module?.permissions?.find((perm:Permission, index:number)=>{
            return perm?.selected === false && perm?.type !== 'ALL'
        }) ? false : true 
    }

    const renderModule = (module: Module) => {
        return (
            <tr>
                <td className="module-name">
                    <div className="form-check form-switch me-2 permissions-switch">
                        <label className="form-check-label mt-0 d-flex align-items-center">
                            <input
                                className="form-check-input me-2"
                                type="checkbox"
                                role="switch"
                                disabled={readOnly}
                                checked={allSelected(module)}
                                onChange={(e:any)=>{
                                    module.permissions = module?.permissions?.map((permission: Permission, index)=>{
                                        return {
                                            ...permission,
                                            selected: e?.target?.checked
                                        }
                                    })

                                    setRole({
                                        ...role
                                    })
                                }}
                            />
                            <span className="ms-1">{module.moduleName}</span>
                        </label>
                    </div>
                </td>
                {
                    module?.permissions?.sort((a: Permission, b: Permission) => a?.type && b?.type ? a?.type?.localeCompare(b?.type) : 0)
                        ?.map((permision: Permission, index) => {
                            return (
                                permision?.type !== 'ALL'
                                ?
                                    <td key={index}>
                                        <div className="form-check d-flex align-items-center permissions-checkbox">
                                            <label className="form-check-label mt-0 d-flex align-items-center">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    disabled={readOnly}
                                                    checked={permision?.selected}
                                                    onChange={(e: any) => {
                                                        permision.selected = e.target.checked
                                                        setRole({
                                                            ...role
                                                        })
                                                    }}
                                                />
                                                <span className="permission-type ms-2">{permision?.type}</span>
                                            </label>
                                        </div>
                                    </td>
                                : null
                            )
                    })
                }
            </tr>
        )
    }

    const renderMsgBox = () => {
        return (
            <div className={`permissions-status-message ${msgBoxSett?.type === 'SUCCESS' ? 'success' : msgBoxSett?.type === 'ERROR' ? 'error' : 'info'}`}>
                <div className="d-flex align-items-center">
                    {
                        msgBoxSett?.type === 'SUCCESS'
                        ?
                        <h6 className="mb-0"> <i className="ti ti-thumb-up me-1"/>{msgBoxSett?.message}</h6>
                        :
                        msgBoxSett?.type === 'ERROR'
                        ?
                        <h6 className="mb-0"> <i className="ti ti-alert-triangle me-1"/>{msgBoxSett?.message}</h6>
                        :
                        <h6 className="mb-0"> <i className="ti ti-info-circle me-1"/> {readOnly ? 'View' : 'Select'} permissions for role <strong>{role?.name}</strong> {readOnly ? '' : 'and click "Save" button'}.</h6>  
                    }
                </div>
            </div>
        )
    }

    return (
        <>
            <PreLoader showLoader={isLoading} />

            <div className="modal fade permissions-modal" id="modify_roles_permissions">
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <div className="d-flex align-items-center">
                                <h4 className="modal-title me-2"> 
                                    {readOnly ? 'View' : 'Update'} Role "<span className="text-primary">{role?.name}</span>" Permissions
                                </h4>
                            </div>
                            <button
                                type="button"
                                className="btn-close custom-btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            >
                                <i className="ti ti-x" />
                            </button>
                        </div>
                        <form action={""}>
                            <div>
                                <div className="modal-body">
                                    {renderMsgBox()}
                                    <div className="table-container">
                                        <table className={`table permissions-table ${readOnly ? 'read-only' : ''}`}>
                                            <thead>
                                                <tr>
                                                    <th>Module</th>
                                                    <th>Create</th>
                                                    <th>Delete</th>
                                                    <th>Edit</th>
                                                    <th>View</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    role?.module?.sort((a: Module, b: Module) => a?.moduleName && b?.moduleName ? a?.moduleName?.localeCompare(b?.moduleName) : 0)
                                                    .map((module, index)=>{  
                                                        return <React.Fragment key={index}>{renderModule(module)}</React.Fragment>
                                                    })
                                                }
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    {readOnly 
                                        ? <button 
                                            type="button"
                                            className="btn btn-secondary"
                                            data-bs-dismiss="modal"
                                          >
                                            Close
                                          </button>
                                        : <FormFooter 
                                            error={msgBoxSett?.type === 'ERROR'} message={msgBoxSett?.message} 
                                            onCancel={onCancel} onSave={update}
                                          />
                                    }  
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    )
}

export default ModifyPermission;